<template lang="pug">
modal-window.modal-teacher-edit(:title='title', ref='modalWindow')
  .modal-teacher-edit__inner(v-if='teacherEditData')
    .modal-teacher-edit__inner__body(ref='modalBody')
      .modal-teacher-edit__inner__body__error
        .modal-teacher-edit__inner__body__error__message.text-small(v-for='message in errorMessages') {{ message }}
      .modal-teacher-edit__inner__body__item
        .modal-teacher-edit__inner__body__item__label.text-small
          slot(name='roleNameLabel')
        pulldown-select.modal-teacher-edit__inner__body__item__select(
          v-model='teacherEditData.role',
          :selectOptions='roleSelectOptions',
          :disabled='disableChangeRole'
        )
      .modal-teacher-edit__inner__body__item(v-if='showAcademySelect')
        .modal-teacher-edit__inner__body__item__label.text-small(v-if='isUpdate')
          slot(name='academyNameDisabledLabel')
        .modal-teacher-edit__inner__body__item__label.text-small(v-else)
          slot(name='academyNameLabel')
        pulldown-select.modal-teacher-edit__inner__body__item__select(
          v-model='teacherEditData.academyId',
          :selectOptions='academySelectOptions',
          :disabled='disableChangeAcademyBranch',
          @input='academyChangeEvent'
        )
      .modal-teacher-edit__inner__body__item(v-if='showBranchSelect')
        .modal-teacher-edit__inner__body__item__label.text-small(v-if='isUpdate')
          slot(name='branchNameDisabledLabel')
        .modal-teacher-edit__inner__body__item__label.text-small(v-else)
          slot(name='branchNameLabel')
        pulldown-select.modal-teacher-edit__inner__body__item__select(
          v-model='teacherEditData.branchId',
          :selectOptions='branchSelectOptions',
          :disabled='disableChangeAcademyBranch'
        )
      .modal-teacher-edit__inner__body__item
        .modal-teacher-edit__inner__body__item__label.text-small
          slot(name='userNameLabel')
        textbox-setting.modal-teacher-edit__inner__body__item__input(
          v-model='teacherEditData.userName',
          :placeholder='userNamePlaceholder',
          :disabled='disableInputName'
        )
      .modal-teacher-edit__inner__body__item
        .modal-teacher-edit__inner__body__item__label.text-small
          slot(name='userBcbtCodeLabel')
        textbox-setting.modal-teacher-edit__inner__body__item__input(
          v-model='teacherEditData.bcbtCode',
          :placeholder='userBcbtCodePlaceholder',
          :disabled='disableInputBcbtCode'
        )
      .modal-teacher-edit__inner__body__item
        .modal-teacher-edit__inner__body__item__label.text-small
          .modal-teacher-edit__inner__body__item__label__value
            slot(name='passwordLabel')
          .modal-teacher-edit__inner__body__item__label__annotation
            slot(name='passwordLabelAnnotation')
        textbox-setting.modal-teacher-edit__inner__body__item__input(
          type='password',
          v-model='teacherEditData.password',
          :placeholder='passwordPlaceholder'
        )
      .modal-teacher-edit__inner__body__item
        .modal-teacher-edit__inner__body__item__label.text-small
          .modal-teacher-edit__inner__body__item__label__value
            slot(name='passwordConfirmLabel')
          .modal-teacher-edit__inner__body__item__label__annotation
            slot(name='passwordConfirmLabelAnnotation')
        textbox-setting.modal-teacher-edit__inner__body__item__input(
          type='password',
          v-model='teacherEditData.passwordConfirm',
          :placeholder='passwordConfirmPlaceholder'
        )
    .modal-teacher-edit__inner__footer
      button-modal-keep.modal-teacher-edit__inner__footer__button(@click='keep')
        slot(name='keepText')
</template>

<script lang="ts">
import { Component, Prop, Vue, Ref } from 'vue-property-decorator'
import ModalWindow from '@/components/molecules/manager/ModalWindow.vue'
import PulldownSelect from '@/components/molecules/manager/PulldownSelect.vue'
import TextboxSetting from '@/components/molecules/manager/TextboxSetting.vue'
import ButtonModalKeep from '@/components/molecules/manager/ButtonModalKeep.vue'
import { TeacherEditType } from '@/models/teacherListModel'
import { UserRoles, UserRoleSettings } from '@/const/UserRoles'
import { InputItem } from '@/models/inputItem'

/**
 * ユーザー管理・先生登録編集モーダル
 */
@Component({
  components: {
    ModalWindow,
    PulldownSelect,
    TextboxSetting,
    ButtonModalKeep,
  },
})
export default class ModalTeacherEdit extends Vue {
  // ユーザ権限
  @Prop()
  userRole!: UserRoles

  // モーダルのタイトル
  @Prop()
  title!: string

  // 編集対象のユーザー（先生・講師）情報
  @Prop()
  teacherEditData!: TeacherEditType

  // 塾名選択プルダウンのオプション（システム管理者画面のみ）
  @Prop()
  academySelectOptions?: InputItem[]

  // 塾名変更時イベント（システム管理者のみ）
  @Prop()
  academyChangeEvent?: Function

  // 教室名選択プルダウンのオプション（システム管理者、塾管理者画面のみ）
  @Prop()
  branchSelectOptions?: InputItem[]

  // 氏名プレースホルダー
  @Prop()
  userNamePlaceholder!: string

  // BCBT Codeプレースホルダー
  @Prop()
  userBcbtCodePlaceholder!: string

  // パスワードプレースホルダー
  @Prop()
  passwordPlaceholder!: string

  // パスワード（確認）プレースホルダー
  @Prop()
  passwordConfirmPlaceholder!: string

  // 保存ボタン押下時イベント
  @Prop()
  keepButtonEvent!: Function

  // エラーメッセージ
  @Prop()
  errorMessages!: string[]

  // モーダルウィンドウのRef
  @Ref()
  modalWindow!: ModalWindow

  // 入力エリア部のHTLMエレメント
  @Ref()
  modalBody!: HTMLElement

  // 保存処理
  private async keep() {
    await this.keepButtonEvent()
    if (this.errorMessages.length > 0) {
      // エラーメッセージがある場合は入力エリア部のトップまでスクロール
      this.modalBody.scrollTo({ top: 0 })
    } else {
      this.modalWindow.hide()
    }
  }

  public show() {
    this.modalWindow.show()
  }

  // ログインユーザ権限から選択可能なユーザ権限一覧を取得、権限プルダウンを生成する
  private get roleSelectOptions(): InputItem[] {
    let configurable = UserRoleSettings.findConfigurableByRole(this.userRole)
    // 更新時かつ編集可能な場合、システム管理者と塾管理者以外の権限が選択対象となる
    if (this.isUpdate && !this.disableChangeRole) {
      configurable = configurable.filter((roleSetting) => {
        return !this.userRoleIsAdminOrAcademy(roleSetting.role)
      })
    }

    return configurable.map((roleSetting) => {
      return { value: String(roleSetting.role), label: roleSetting.name }
    })
  }

  // ログインユーザがシステム管理者かつ選択権限がシステム管理者以外の場合のみ塾選択を表示
  private get showAcademySelect(): boolean {
    return this.userRole === UserRoles.ADMIN && this.teacherEditData.role !== UserRoles.ADMIN
  }

  // ログインユーザがシステム管理者または塾管理者かつ選択権限がシステム管理者と塾管理者以外の場合のみ教室選択を表示
  private get showBranchSelect(): boolean {
    return this.userRoleIsAdminOrAcademy(this.userRole) && !this.userRoleIsAdminOrAcademy(this.teacherEditData.role)
  }

  // 更新処理であるか
  private get isUpdate(): boolean {
    return !!this.teacherEditData?.userId
  }

  // システム管理者であるか
  private get isAdmin(): boolean {
    return this.userRole === UserRoles.ADMIN
  }

  // 権限変更不可フラグ
  private get disableChangeRole(): boolean {
    // システム管理者以外、または更新処理かつ権限プルダウンでシステム管理者か塾管理者が選択されている場合、変更不可
    return !this.isAdmin || (this.isUpdate && this.userRoleIsAdminOrAcademy(this.teacherEditData.role))
  }

  // 塾/教室変更不可フラグ
  private get disableChangeAcademyBranch(): boolean {
    // システム管理者以外、または更新処理の場合、変更不可
    return !this.isAdmin || this.isUpdate
  }

  // 氏名入力不可フラグ
  private get disableInputName(): boolean {
    // システム管理者以外は入力不可
    return !this.isAdmin
  }

  // BCBTコード入力不可フラグ
  private get disableInputBcbtCode(): boolean {
    // システム管理者以外は入力不可
    return !this.isAdmin || this.isUpdate
  }

  private userRoleIsAdminOrAcademy(role: UserRoles | string) {
    return role === UserRoles.ADMIN || role === UserRoles.ACADEMY
  }
}
</script>

<style lang="stylus" scoped>
.modal-teacher-edit
  &__inner
    display flex
    height 463px
    flex-direction column
    align-items flex-start
    gap 20px

    &__body
      display flex
      flex-direction column
      gap 12px
      overflow-y auto

      &__error
        &__message
          color var(--color-manager-modal-alert__text)

      &__item
        width 100%
        display flex
        flex-direction column
        gap 12px

        &__select
          width 100%

        &__label
          display flex
          justify-content space-between

    &__footer
      margin-top auto
</style>
